import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"
import { cn } from "@/shadcn/lib/utils"
import type { LucideIcon } from "lucide-react"
import { Icon, type IconProps } from "@/shared/components/Icon"
import { Spinner, type SpinnerProps } from "@/shadcn/components/ui/spinner"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        accent:
          "bg-accent-foreground text-background hover:bg-accent-foreground/80 aria-expanded:bg-muted aria-expanded:text-foreground",
        outline:
          "border-border bg-background text-foreground hover:border-foreground hover:bg-muted aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        "outline-primary":
          "border-primary bg-background text-primary hover:bg-primary/10 aria-expanded:bg-primary/10 dark:border-primary dark:hover:bg-primary/20",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        "ghost-primary": "text-primary hover:bg-primary/10 aria-expanded:bg-primary aria-expanded:text-foreground",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-8",
        "icon-xs":
          "size-5 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
    iconLeft?: LucideIcon
    iconRight?: LucideIcon
    iconLeftProps?: Omit<IconProps, "icon" | "data-icon">
    iconRightProps?: Omit<IconProps, "icon" | "data-icon">
    iconProps?: Omit<IconProps, "icon" | "data-icon">
    spinnerProps?: SpinnerProps
  }

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  children,
  iconLeft,
  iconRight,
  iconLeftProps,
  iconRightProps,
  iconProps,
  spinnerProps,
  ...props
}: ButtonProps) {
  const loadingSpinner = loading ? (
    <span className="absolute inset-0 inline-flex items-center justify-center">
      <Spinner size="1.4em" {...spinnerProps} />
    </span>
  ) : null

  if (asChild) {
    return (
      <Slot.Root
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }), {
          relative: loading,
          "pointer-events-none": loading || props.disabled,
        })}
        aria-busy={loading || undefined}
        {...props}
      >
        <Slot.Slottable>{children}</Slot.Slottable>
        {loadingSpinner}
      </Slot.Root>
    )
  }

  const buttonBody = (
    <>
      {iconLeft && <Icon icon={iconLeft} data-icon="inline-start" {...iconLeftProps} {...iconProps} />}
      {children}
      {iconRight && <Icon icon={iconRight} data-icon="inline-end" {...iconRightProps} {...iconProps} />}
    </>
  )

  return (
    <button
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }), {
        relative: loading,
        "pointer-events-none": loading || props.disabled,
      })}
      aria-busy={loading || undefined}
      {...props}
    >
      <span className={cn("inline-flex items-center justify-center gap-[inherit]", loading && "invisible")}>
        {buttonBody}
      </span>
      {loadingSpinner}
    </button>
  )
}

export { Button, buttonVariants }
